import { useState } from 'react'
import Modal from '../components/Modal'
import { ErrorText, Field, Row, Text } from '../components/Form'
import { saveVenue, deleteVenue } from '../lib/mutations'
import type { VenueEntry } from '../lib/database'

/**
 * Add or edit a venue.
 *
 * A venue reached from a played show has no `venues` document behind it yet —
 * saving one here creates it, which is how a room you've played becomes a room
 * you've got details for.
 */
export default function VenueDialog({
  open,
  venue,
  ownerId,
  onClose,
}: {
  open: boolean
  venue?: VenueEntry | null
  ownerId: string
  onClose: () => void
}) {
  const [draft, setDraft] = useState(() => ({
    name: venue?.name ?? '',
    city: venue?.city ?? '',
    country: venue?.country ?? '',
    address: venue?.address ?? '',
    capacity: venue?.capacity ? String(venue.capacity) : '',
    contactName: venue?.contactName ?? '',
    contactEmail: venue?.contactEmail ?? '',
    contactPhone: venue?.contactPhone ?? '',
  }))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)

  const patch = (next: Partial<typeof draft>) =>
    setDraft((previous) => ({ ...previous, ...next }))
  const valid = draft.name.trim().length > 0 && draft.city.trim().length > 0

  // A venue that only exists because you played it has no document to update.
  const existingId = venue?.saved ? venue.id : undefined

  async function save() {
    if (!valid) return
    setBusy(true)
    setError(null)
    try {
      await saveVenue(
        {
          name: draft.name,
          city: draft.city,
          country: draft.country || undefined,
          address: draft.address || undefined,
          capacity: draft.capacity ? Number(draft.capacity) : undefined,
          contactName: draft.contactName || undefined,
          contactEmail: draft.contactEmail || undefined,
          contactPhone: draft.contactPhone || undefined,
        },
        ownerId,
        existingId,
      )
      onClose()
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      title={existingId ? 'Edit venue' : venue ? 'Save this venue' : 'New venue'}
      onClose={onClose}
      footer={
        <>
          {existingId && (
            <button
              className="btn btn-danger"
              style={{ marginRight: 'auto' }}
              disabled={busy}
              onClick={() => setConfirming(true)}
            >
              Delete
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={!valid || busy}>
            {busy ? 'Saving…' : 'Save venue'}
          </button>
        </>
      }
    >
      <div className="stack">
        {venue && !venue.saved && (
          <p
            className="t-caption"
            style={{
              padding: '10px 13px',
              borderRadius: 'var(--radius-control)',
              background: 'var(--accent-wash)',
              color: 'var(--accent-text)',
            }}
          >
            This venue came from a show you've played. Saving it adds it to your database so the
            details stick around for next time.
          </p>
        )}

        <Field label="Venue name">
          <Text value={draft.name} onChange={(value) => patch({ name: value })} autoFocus />
        </Field>

        <Field label="Address">
          <Text value={draft.address} onChange={(value) => patch({ address: value })} />
        </Field>

        <Row columns={3}>
          <Field label="City">
            <Text value={draft.city} onChange={(value) => patch({ city: value })} />
          </Field>
          <Field label="Country">
            <Text value={draft.country} onChange={(value) => patch({ country: value })} />
          </Field>
          <Field label="Capacity">
            <Text
              type="number"
              value={draft.capacity}
              onChange={(value) => patch({ capacity: value })}
            />
          </Field>
        </Row>

        <section className="stack">
          <span className="t-eyebrow">Venue contact</span>
          <Field label="Name">
            <Text
              value={draft.contactName}
              onChange={(value) => patch({ contactName: value })}
            />
          </Field>
          <Row>
            <Field label="Email">
              <Text
                type="email"
                value={draft.contactEmail}
                onChange={(value) => patch({ contactEmail: value })}
              />
            </Field>
            <Field label="Phone">
              <Text
                value={draft.contactPhone}
                onChange={(value) => patch({ contactPhone: value })}
              />
            </Field>
          </Row>
        </section>

        <ErrorText>{error}</ErrorText>

        {confirming && existingId && (
          <div
            className="stack"
            style={{
              gap: 8,
              padding: 14,
              borderRadius: 'var(--radius-control)',
              background: 'color-mix(in srgb, var(--destructive) 10%, transparent)',
            }}
          >
            <strong>Delete {draft.name}?</strong>
            <p className="t-caption">
              Shows already booked here keep their details. It only comes out of your database.
            </p>
            <div className="row" style={{ gap: 8 }}>
              <button
                className="btn btn-danger"
                disabled={busy}
                onClick={async () => {
                  setBusy(true)
                  try {
                    await deleteVenue(existingId)
                    onClose()
                  } catch (caught) {
                    setError((caught as Error).message)
                  } finally {
                    setBusy(false)
                  }
                }}
              >
                {busy ? 'Deleting…' : 'Delete venue'}
              </button>
              <button className="btn btn-quiet" onClick={() => setConfirming(false)}>
                Keep it
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
